import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Anchor,
    Box,
    Button,
    CheckBox,
    DataTable,
    Heading,
    Layer,
    Select,
    Text,
    TextInput,
    Tip,
} from 'grommet';
import { Checkmark, Search, StatusGood } from 'grommet-icons';
import {
    API,
    ConfirmDeleteButton,
    MessageButton,
    SearchSelect,
} from '@enrich/shared-lib';
import {
    useTodosInfinite,
    DEFAULT_SORT,
    isDefaultSort,
} from './useTodosInfinite';

const userLabel = (user) => {
    if (!user) return '';
    const full = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return full || user.email || '';
};

export const assigneeName = (todo) => {
    if (!todo.assignee) return 'Unassigned';
    return userLabel(todo.assignee) || 'Unassigned';
};

const ASSIGNMENT_OPTIONS = [
    { label: 'All', value: 'all' },
    { label: 'Assigned', value: 'assigned' },
    { label: 'Unassigned', value: 'unassigned' },
];

export const TasksTable = ({
    revalidator,
    todos,
    pagination,
    assigneeId,
    canAssign = false,
    canDelete = false,
    assignableUsers = [],
    showAssignmentFilter = false,
    showContractClientFilter = false,
}) => {
    const [assignment, setAssignment] = useState('all');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [contractClientsOnly, setContractClientsOnly] = useState(false);
    // Sorting is done by the API, not by Grommet, so a sort covers every
    // matching task and not only the pages already loaded.
    const [sort, setSort] = useState(DEFAULT_SORT);

    const filtersActive =
        assignment !== 'all' ||
        searchInput !== '' ||
        contractClientsOnly ||
        !isDefaultSort(sort);

    const clearFilters = () => {
        setAssignment('all');
        setSearchInput('');
        setSearch('');
        setContractClientsOnly(false);
        setSort(DEFAULT_SORT);
    };

    // Debounce the assignee search so we re-query on pause, not per keystroke.
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const { allTodos, loadMore, loading, hasMore } = useTodosInfinite(
        todos,
        pagination,
        { assigneeId, assignment, search, contractClientsOnly, sort },
    );

    const dataColumns = [
        {
            header: 'Student',
            property: 'student',
            sortable: true,
            render: (todo) => {
                if (!todo.contact) {
                    return <Text key={todo.id}>[student data missing]</Text>;
                }
                return (
                    <Anchor
                        key={todo.id}
                        href={`/contacts/${todo.contact.id}`}
                        label={`${todo.contact.firstName} ${todo.contact.lastName}`}
                    />
                );
            },
        },
        {
            header: 'Task',
            property: 'task',
            sortable: true,
            render: (todo) => {
                return <TodoLabel todo={todo} key={todo.id} />;
            },
        },
        {
            header: 'Message',
            property: 'task.id',
            sortable: false,
            render: (todo) => {
                return <MessageButton contact={todo.contact} iconOnly />;
            },
        },
        {
            header: 'Assignee',
            property: 'assignee',
            sortable: true,
            render: (todo) => {
                return <Text key={todo.id}>{assigneeName(todo)}</Text>;
            },
        },
        {
            header: 'Created',
            property: 'createdAt',
            sortable: true,
            render: (todo) => {
                return (
                    <Text key={todo.id}>
                        {todo.createdAt
                            ? new Date(todo.createdAt).toLocaleDateString()
                            : '—'}
                    </Text>
                );
            },
        },
        {
            header: 'Actions',
            property: 'completed',
            sortable: false,
            render: (todo) => {
                return (
                    <TodoActions
                        todo={todo}
                        key={todo.id}
                        revalidator={revalidator}
                        canAssign={canAssign}
                        canDelete={canDelete}
                        assignableUsers={assignableUsers}
                    />
                );
            },
        },
    ];

    return (
        <Box pad="medium" elevation="large" gap="medium">
            {(showAssignmentFilter || showContractClientFilter) && (
                <Box
                    direction="row"
                    align="center"
                    gap="medium"
                    wrap
                    justify="between">
                    {showAssignmentFilter && (
                        <Box direction="row" align="center" gap="small">
                            <Text size="small">Show:</Text>
                            <Box width="small">
                                <Select
                                    options={ASSIGNMENT_OPTIONS}
                                    labelKey="label"
                                    valueKey={{ key: 'value', reduce: true }}
                                    value={assignment}
                                    onChange={({ value }) =>
                                        setAssignment(value)
                                    }
                                />
                            </Box>
                        </Box>
                    )}
                    {showAssignmentFilter && (
                        <Box width="medium">
                            <TextInput
                                icon={<Search />}
                                placeholder="Search by assignee"
                                value={searchInput}
                                onChange={(event) =>
                                    setSearchInput(event.target.value)
                                }
                            />
                        </Box>
                    )}
                    {showContractClientFilter && (
                        <CheckBox
                            label="Contract clients only"
                            checked={contractClientsOnly}
                            onChange={({ target }) =>
                                setContractClientsOnly(target.checked)
                            }
                            toggle
                        />
                    )}
                    {filtersActive && (
                        <Button label="Clear filters" onClick={clearFilters} />
                    )}
                </Box>
            )}
            <DataTable
                columns={dataColumns}
                data={allTodos}
                primaryKey="id"
                sort={{ ...sort, external: true }}
                onSort={({ property, direction }) =>
                    setSort({ property, direction })
                }
            />
            {hasMore && (
                <Box align="center">
                    <Button
                        label={loading ? 'Loading…' : 'Load more'}
                        disabled={loading}
                        onClick={loadMore}
                    />
                </Box>
            )}
            {!hasMore && allTodos.length > 0 && (
                <Box align="center">
                    <Text size="small" color="dark-4">
                        All {allTodos.length} tasks loaded
                    </Text>
                </Box>
            )}
        </Box>
    );
};

const TodoActions = ({
    todo,
    revalidator,
    canAssign,
    canDelete,
    assignableUsers,
}) => {
    const [approvalButtonBusy, setApprovalButtonBusy] = useState(false);

    const markCompleted = async () => {
        setApprovalButtonBusy(true);
        try {
            await API.put(`/api/to-dos/${todo.id}`, {
                data: {
                    completed: true,
                },
            });
            revalidator.revalidate();
        } catch (error) {
            console.log({ todoUpdateError: error });
        }
        setApprovalButtonBusy(false);
    };

    return (
        <Box direction="row" gap="small" align="center">
            {todo.completed ? (
                <Tip content="Completed">
                    <Button
                        icon={<StatusGood color="status-ok" />}
                        a11yTitle="Completed"
                        disabled
                    />
                </Tip>
            ) : (
                <Tip content="Mark completed">
                    <Button
                        icon={<Checkmark />}
                        a11yTitle="Mark completed"
                        busy={approvalButtonBusy}
                        onClick={markCompleted}
                    />
                </Tip>
            )}
            {canAssign && !todo.assignee && (
                <AssignButton
                    todo={todo}
                    users={assignableUsers}
                    revalidator={revalidator}
                />
            )}
            {canDelete && (
                <DeleteButton todo={todo} revalidator={revalidator} />
            )}
        </Box>
    );
};

const AssignButton = ({ todo, users, revalidator }) => {
    const [showModal, setShowModal] = useState(false);
    const [busy, setBusy] = useState(false);

    const assign = async (user) => {
        if (!user) return;
        setBusy(true);
        try {
            await API.put(`/api/to-dos/${todo.id}`, {
                data: { assignee: user.id },
            });
            revalidator.revalidate();
            setShowModal(false);
        } catch (error) {
            console.log({ todoAssignError: error });
        }
        setBusy(false);
    };

    return (
        <Box>
            <Button label="Assign" onClick={() => setShowModal(true)} />
            {showModal && (
                <Layer
                    onClickOutside={() => setShowModal(false)}
                    onEsc={() => setShowModal(false)}
                    position="center">
                    <Box
                        background="white"
                        pad="medium"
                        round="6px"
                        border={{ color: 'enrich-green-light', size: '3px' }}
                        gap="medium"
                        width="medium">
                        <Heading level="4" margin="none">
                            Assign task
                        </Heading>
                        <SearchSelect
                            placeholder="Select a user"
                            labelKey={userLabel}
                            valueKey="id"
                            options={users}
                            updateFormData={assign}
                            disabled={busy}
                        />
                        <Box direction="row" justify="end">
                            <Button
                                label="Cancel"
                                onClick={() => setShowModal(false)}
                            />
                        </Box>
                    </Box>
                </Layer>
            )}
        </Box>
    );
};

const DeleteButton = ({ todo, revalidator }) => {
    const remove = async () => {
        await API.delete(`/api/to-dos/${todo.id}`);
        revalidator.revalidate();
    };

    return (
        <ConfirmDeleteButton
            a11yTitle="Delete task"
            message="Delete this task? This cannot be undone."
            onConfirm={remove}
        />
    );
};

const TodoLabel = ({ todo }) => {
    const { candidacy, contact, task } = todo;
    const job = candidacy?.job;

    if (!task) {
        return <Box key={todo.id}>[task data missing]</Box>;
    }

    if (task.indexModel === 'candidacy') {
        return (
            <Text key={todo.id}>
                {todo.task.title} for{' '}
                {job ? (
                    <Anchor
                        key={todo.id}
                        href={`/jobs/${job.id}`}
                        label={job.title}
                    />
                ) : (
                    '[job data missing]'
                )}
            </Text>
        );
    }

    if (todo.task.specialType === 'message') {
        return (
            <Box key={todo.id}>
                <Text>
                    {contact ? (
                        <Anchor
                            key={todo.id}
                            href={`/contacts/${contact.id}/conversations`}
                            label={task.title}
                        />
                    ) : (
                        task.title
                    )}
                </Text>
            </Box>
        );
    }

    return (
        <Box key={todo.id}>
            <Text>{task.title}</Text>
        </Box>
    );
};

TasksTable.propTypes = {
    revalidator: PropTypes.object,
    todos: PropTypes.array,
    pagination: PropTypes.object,
    assigneeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    canAssign: PropTypes.bool,
    canDelete: PropTypes.bool,
    assignableUsers: PropTypes.array,
    showAssignmentFilter: PropTypes.bool,
    showContractClientFilter: PropTypes.bool,
};

TodoActions.propTypes = {
    revalidator: PropTypes.object,
    todo: PropTypes.object,
    canAssign: PropTypes.bool,
    canDelete: PropTypes.bool,
    assignableUsers: PropTypes.array,
};

AssignButton.propTypes = {
    todo: PropTypes.object,
    users: PropTypes.array,
    revalidator: PropTypes.object,
};

DeleteButton.propTypes = {
    todo: PropTypes.object,
    revalidator: PropTypes.object,
};

TodoLabel.propTypes = {
    todo: PropTypes.object,
};
